import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Empty,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { RadarChart, ScoreHistogram } from './Admin360Charts';

const { Text, Title } = Typography;

const score = (value, digits = 1) =>
  Number.isFinite(Number(value)) ? Number(value).toFixed(digits) : '—';

const gap = (value) => {
  if (!Number.isFinite(Number(value))) return '—';
  const numeric = Number(value);
  return `${numeric > 0 ? '+' : ''}${numeric.toFixed(1)}`;
};

const scoreColumns = (roles, firstColumn) => [
  firstColumn,
  ...roles.map((role) => ({
    title: role.label,
    key: role.key,
    align: 'center',
    width: 90,
    render: (_, record) => score(record.scores?.[role.key]),
  })),
  {
    title: '他者平均',
    key: 'others',
    align: 'center',
    width: 100,
    render: (_, record) => <b>{score(record.scores?.others)}</b>,
  },
];

const QuestionRanking = ({ title, description, questions, tone }) => (
  <Card title={title} className="admin360__card admin360__ranking-card">
    <Text type="secondary">{description}</Text>
    <div className="admin360__question-ranking">
      {questions.map((question, index) => (
        <div
          key={question.no}
          className={`admin360__question-rank admin360__question-rank--${tone}`}
        >
          <span>{index + 1}</span>
          <div>
            <b>Q{question.no}</b> {question.text}
            <small>{question.category}</small>
          </div>
          <strong>{score(question.score, 2)}</strong>
        </div>
      ))}
    </div>
  </Card>
);

const Admin360OrganizationView = ({
  data,
  selectedSeasonId,
  onSeasonChange,
  seasonOptions,
}) => {
  const [sortMode, setSortMode] = useState('othersDesc');
  const season = data?.season;
  const report = data?.report;
  const roles = report?.roles || [];

  const targets = useMemo(() => {
    const rows = [...(report?.targets || [])];
    const sorters = {
      othersDesc: (left, right) =>
        (right.scores.others ?? -1) - (left.scores.others ?? -1),
      othersAsc: (left, right) =>
        (left.scores.others ?? 9) - (right.scores.others ?? 9),
      gapDesc: (left, right) => (right.gap ?? -9) - (left.gap ?? -9),
      gapAsc: (left, right) => (left.gap ?? 9) - (right.gap ?? 9),
      name: (left, right) => left.name.localeCompare(right.name, 'ja'),
    };
    return rows.sort(sorters[sortMode]);
  }, [report, sortMode]);

  if (!report) {
    return <Empty description="組織レポートを表示できません" />;
  }

  const categories = report.categories || [];
  const upperCategories = report.upperCategories || [];
  const showUpperCategories =
    upperCategories.length > 1 &&
    (upperCategories.length !== categories.length ||
      upperCategories.some(
        (category, index) => category.name !== categories[index]?.name
      ));
  const categoryColumns = scoreColumns(roles, {
    title: report.format.categoryLabel,
    dataIndex: 'name',
    key: 'name',
    width: 240,
  });
  const upperCategoryColumns = scoreColumns(roles, {
    title: report.format.upperCategoryLabel,
    dataIndex: 'name',
    key: 'name',
    width: 240,
  });
  const departmentColumns = scoreColumns(roles, {
    title: '部署',
    dataIndex: 'name',
    key: 'name',
    width: 220,
  });
  departmentColumns.splice(1, 0, {
    title: '対象者',
    dataIndex: 'targetCount',
    key: 'targetCount',
    align: 'center',
    width: 90,
    render: (value) => `${value}名`,
  });
  departmentColumns.push({
    title: 'ギャップ',
    dataIndex: 'gap',
    key: 'gap',
    align: 'center',
    width: 100,
    render: (value) => gap(value),
  });

  const targetColumns = [
    {
      title: '氏名',
      dataIndex: 'name',
      key: 'name',
      width: 160,
      render: (name) => <b>{name}</b>,
    },
    {
      title: '所属',
      dataIndex: 'departments',
      key: 'departments',
      width: 260,
      render: (departments) => departments.join(' 〉 ') || '—',
    },
    ...roles.map((role) => ({
      title: role.label,
      key: role.key,
      align: 'center',
      width: 85,
      render: (_, record) => score(record.scores?.[role.key]),
    })),
    {
      title: '他者平均',
      key: 'others',
      align: 'center',
      width: 100,
      render: (_, record) => <b>{score(record.scores?.others)}</b>,
    },
    {
      title: 'ギャップ',
      dataIndex: 'gap',
      key: 'gap',
      align: 'center',
      width: 95,
      render: (value) => gap(value),
    },
    {
      title: '協力者',
      dataIndex: 'collaboratorCount',
      key: 'collaboratorCount',
      align: 'center',
      width: 80,
      render: (value) => `${value}名`,
    },
    {
      title: '',
      key: 'result',
      align: 'center',
      width: 115,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          href={
            record.subjectTokenId
              ? `/results/${season.surveyCode}/${encodeURIComponent(
                  record.subjectTokenId
                )}?view=admin&from=admin360`
              : undefined
          }
          target="_blank"
          rel="noopener noreferrer"
          disabled={!record.resultAvailable}
        >
          Webで見る
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className="admin360__heading admin360__org-title">
        <div>
          <Title level={2}>組織レポート</Title>
          <Text type="secondary">
            {season.name} ｜ {season.surveyCode} / {season.type360}
          </Text>
        </div>
        <Space wrap>
          <Tag color="processing">
            実回答ベース {report.kpis.targetCount}/{report.kpis.assignedTargetCount}名
          </Tag>
          <Select
            value={selectedSeasonId}
            onChange={onSeasonChange}
            options={seasonOptions}
            className="admin360__season-select"
          />
        </Space>
      </div>

      <Row gutter={[16, 16]} className="admin360__kpis">
        <Col xs={24} sm={12} xl={4}>
          <Card>
            <Statistic title="対象者" value={report.kpis.targetCount} suffix="名" />
          </Card>
        </Col>
        {roles.map((role) => (
          <Col xs={24} sm={12} xl={4} key={role.key}>
            <Card>
              <Statistic
                title={`${role.label}評価 平均`}
                value={score(report.kpis.scores?.[role.key])}
                suffix="/5.0"
              />
            </Card>
          </Col>
        ))}
        <Col xs={24} sm={12} xl={4}>
          <Card>
            <Statistic
              title="ギャップ（他者−本人）"
              value={gap(report.kpis.gap)}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card
            title={`${report.format.categoryLabel}別 組織平均`}
            className="admin360__card"
          >
            <RadarChart categories={categories} roles={roles} />
            <div className="admin360__chart-legend">
              {roles.map((role) => (
                <span key={role.key}>
                  <i style={{ background: role.color }} /> {role.label}
                </span>
              ))}
            </div>
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card title="スコア分布（他者評価 総合）" className="admin360__card">
            <Text type="secondary">
              対象者ごとの上司・同僚・部下の全回答平均を0.5刻みで表示
            </Text>
            <ScoreHistogram distribution={report.distribution} />
          </Card>
        </Col>
      </Row>

      {showUpperCategories && (
        <Card
          title={`${report.format.upperCategoryLabel}別スコア（組織平均）`}
          className="admin360__card"
        >
          <Table
            rowKey="key"
            columns={upperCategoryColumns}
            dataSource={upperCategories}
            pagination={false}
            scroll={{ x: 760 }}
          />
        </Card>
      )}

      <Card
        title={`${report.format.categoryLabel}別スコア（組織平均）`}
        className="admin360__card"
      >
        <Table
          rowKey="key"
          columns={categoryColumns}
          dataSource={categories}
          pagination={false}
          scroll={{ x: 760 }}
        />
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <QuestionRanking
            title="組織の強み（他者評価 上位5設問）"
            description="全対象者に対する他者評価の設問平均が高い順"
            questions={report.strengths}
            tone="good"
          />
        </Col>
        <Col xs={24} xl={12}>
          <QuestionRanking
            title="組織の伸びしろ（他者評価 下位5設問）"
            description="全対象者に対する他者評価の設問平均が低い順"
            questions={report.growthAreas}
            tone="bad"
          />
        </Col>
      </Row>

      <Card title="部署別比較" className="admin360__card">
        <Text type="secondary">対象者の所属（第1階層）ごとの直接平均</Text>
        <Table
          rowKey="name"
          columns={departmentColumns}
          dataSource={report.departments}
          pagination={false}
          scroll={{ x: 900 }}
        />
      </Card>

      <Card
        title="対象者一覧（ランキング）"
        className="admin360__card"
        extra={
          <Space>
            <Text type="secondary">並び順</Text>
            <Select
              value={sortMode}
              onChange={setSortMode}
              className="admin360__ranking-sort"
              options={[
                { value: 'othersDesc', label: '他者評価が高い順' },
                { value: 'othersAsc', label: '他者評価が低い順' },
                { value: 'gapDesc', label: 'ギャップが大きい順' },
                { value: 'gapAsc', label: 'ギャップが小さい順' },
                { value: 'name', label: '氏名順' },
              ]}
            />
          </Space>
        }
      >
        <Table
          rowKey="id"
          columns={targetColumns}
          dataSource={targets}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 1220 }}
        />
        <Text type="secondary">
          ギャップが正＝他者評価の方が高い、負＝本人評価の方が高い
        </Text>
      </Card>
    </>
  );
};

export default Admin360OrganizationView;
